// components/use-case-details/use-case-implementation-journey.tsx
"use client";

import { motion } from "framer-motion";

interface ImplementationJourneyProps {
  totalWeeks: number;
  phases: Array<{
    weeks: string;
    title: string;
    description: string;
  }>;
}

export function ImplementationJourney({ totalWeeks, phases }: ImplementationJourneyProps) {
  return (
    <div className="my-12">
      <h2 className="text-2xl font-display font-bold text-white mb-6">
        Implementation Steps (No BS)
      </h2>
      
      <div className="relative border-l-2 border-medium-gray pl-6 py-4">
        {phases.map((phase, index) => (
          <motion.div 
            key={index}
            className="mb-8 relative"
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            {/* Timeline dot */}
            <div className="absolute -left-[30px] top-0 w-4 h-4 rounded-full bg-teal border-4 border-black" />
            
            {/* Week indicator */}
            <div className="bg-medium-gray px-3 py-1 rounded-full inline-block text-sm text-white mb-2">
              Week {phase.weeks}
            </div>
            
            <h3 className="text-xl font-display font-bold text-white mb-2">{phase.title}</h3>
            <p className="text-gray-300">{phase.description}</p>
          </motion.div>
        ))}
        
        {/* Final timeline dot */}
        <div className="absolute -left-[22px] bottom-0 w-4 h-4 rounded-full bg-teal border-4 border-black" />
        
        {/* Total weeks indicator */}
        <div className="mt-4 inline-block bg-teal text-black px-4 py-2 rounded-lg font-display font-bold">
          Total: {totalWeeks} Weeks
        </div>
      </div>
    </div>
  );
}

interface KeyLearningsProps {
  challenges: string[];
  successes: string[];
  tips: string[];
}

export function KeyLearnings({ challenges, successes, tips }: KeyLearningsProps) {
  return (
    <div className="my-12">
      <h2 className="text-2xl font-display font-bold text-white mb-6">
        Key Learnings & Challenges
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#331F1F] p-6 rounded-xl border-t-2 border-orange">
          <h3 className="text-xl font-display font-bold text-white mb-4">Challenges Faced</h3>
          <ul className="space-y-3">
            {challenges.map((challenge, index) => (
              <li key={index} className="flex items-start">
                <span className="text-orange mr-2">→</span>
                <span className="text-gray-300">{challenge}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-[#233323] p-6 rounded-xl border-t-2 border-[#50C878]">
          <h3 className="text-xl font-display font-bold text-white mb-4">Unexpected Wins</h3>
          <ul className="space-y-3">
            {successes.map((success, index) => (
              <li key={index} className="flex items-start">
                <span className="text-[#50C878] mr-2">→</span>
                <span className="text-gray-300">{success}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-bg-dark p-6 rounded-xl border-t-2 border-teal">
          <h3 className="text-xl font-display font-bold text-white mb-4">Pro Tips</h3>
          <ul className="space-y-3">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start">
                <span className="text-teal mr-2">→</span>
                <span className="text-gray-300">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

interface MetricGridCardProps {
  title: string;
  value: string;
  description: string;
  highlight?: boolean;
}

export function MetricGridCard({ title, value, description, highlight = false }: MetricGridCardProps) {
  return (
    <motion.div 
      className="bg-bg-dark p-6 rounded-xl border border-medium-gray relative overflow-hidden"
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3 }}
    >
      <h3 className="text-xl font-display font-bold text-white mb-2">{title}</h3>
      <p className={`text-3xl font-display font-bold ${highlight ? 'text-teal' : 'text-white'} mb-2`}>{value}</p>
      <p className="text-gray-300">{description}</p>
      
      {highlight && (
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-teal/20 rounded-full blur-3xl" />
      )}
    </motion.div>
  );
}

interface ROIShowcaseProps {
  metrics: Array<{
    title: string;
    value: string;
    description: string;
    highlight?: boolean;
  }>;
}

export function ROIShowcase({ metrics }: ROIShowcaseProps) {
  return (
    <div className="my-12">
      <h2 className="text-2xl font-display font-bold text-white mb-2">
        Real Business Impact (Show Me the Money!)
      </h2>
      <p className="text-gray-300 mb-6">Here's what the implementation achieved in measurable terms:</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((metric, index) => (
          <MetricGridCard
            key={index}
            title={metric.title}
            value={metric.value}
            description={metric.description}
            highlight={metric.highlight}
          />
        ))}
      </div>
    </div>
  );
}